import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../App';

// Currency symbols mapping
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥'
};

// Currency Context
const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export function CurrencyProvider({ children, user }) {
  const [rates, setRates] = useState({ USD: 1, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.53, JPY: 149.50 });
  const [preferredCurrency, setPreferredCurrency] = useState(user?.preferred_currency || 'USD');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRates();
  }, []);

  useEffect(() => {
    if (user?.preferred_currency) {
      setPreferredCurrency(user.preferred_currency);
    }
  }, [user?.preferred_currency]);

  const fetchRates = async () => {
    try {
      const res = await api.get('/currency/rates');
      // API returns rates directly, not wrapped in 'rates' object
      setRates(res.data);
    } catch (error) {
      console.error('Failed to fetch currency rates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convert USD to preferred currency
  const convert = (amountUSD, toCurrency = preferredCurrency) => {
    if (!amountUSD || isNaN(amountUSD)) return 0;
    const rate = rates[toCurrency] || 1;
    return amountUSD * rate;
  };

  // Convert from any currency to USD
  const toUSD = (amount, fromCurrency) => {
    if (!amount || isNaN(amount)) return 0;
    const rate = rates[fromCurrency] || 1;
    return amount / rate;
  };

  // Format currency with symbol
  const formatCurrency = (amountUSD, options = {}) => {
    const {
      currency = preferredCurrency,
      showSymbol = true,
      decimals = 2,
      compact = false
    } = options;

    const converted = convert(amountUSD, currency);
    const symbol = showSymbol ? (CURRENCY_SYMBOLS[currency] || '$') : '';
    
    if (compact) {
      if (Math.abs(converted) >= 1000000) {
        return `${symbol}${(converted / 1000000).toFixed(2)}M`;
      }
      if (Math.abs(converted) >= 1000) {
        return `${symbol}${(converted / 1000).toFixed(1)}K`;
      }
    }

    // Handle JPY differently (no decimals typically)
    const actualDecimals = currency === 'JPY' ? 0 : decimals;
    
    return `${symbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: actualDecimals,
      maximumFractionDigits: actualDecimals
    })}`;
  };

  // Get just the symbol
  const getSymbol = (currency = preferredCurrency) => {
    return CURRENCY_SYMBOLS[currency] || '$';
  };

  // Update user's currency preference
  const setCurrency = async (newCurrency) => {
    try {
      await api.post(`/currency/set-preference?currency=${newCurrency}`);
      setPreferredCurrency(newCurrency);
      return true;
    } catch (error) {
      console.error('Failed to set currency preference:', error);
      return false;
    }
  };

  const value = {
    rates,
    preferredCurrency,
    loading,
    convert,
    toUSD,
    formatCurrency,
    getSymbol,
    setCurrency,
    CURRENCY_SYMBOLS
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export default CurrencyContext;
